import { useEffect, useState } from "react";

import type { TimeRange } from "lib/webrtc/debugLogStore";
import { useWebRTCDebug } from "providers/WebRTCDebugProvider";

type Preset = "30m" | "1h" | "1d" | "all" | "custom";

const PRESETS: { id: Exclude<Preset, "custom">; label: string; minutes: number | null }[] = [
    { id: "30m", label: "Last 30 min", minutes: 30 },
    { id: "1h", label: "Last hour", minutes: 60 },
    { id: "1d", label: "Last day", minutes: 24 * 60 },
    { id: "all", label: "All", minutes: null },
];

/** `datetime-local` value (local time, minute precision) for an epoch time. */
const toInputValue = (ms: number): string => {
    const date = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000);
    return date.toISOString().slice(0, 16);
};

const fromInputValue = (value: string): number | null => {
    if (!value) return null;
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? null : ms;
};

const buttonClass = (active: boolean) =>
    `rounded-r5 border px-2 py-0.5 text-h5 ${
        active ? "border-primary2 bg-primary2 font-semibold text-white" : "border-black70 bg-black90 text-black40 hover:text-white"
    }`;

/** Time-range controls for the stored log; loads once on mount with the first preset. */
const DevWebrtcHistoryBar: React.FC = () => {
    const { history, loadHistory } = useWebRTCDebug();
    const [preset, setPreset] = useState<Preset>("30m");
    const [from, setFrom] = useState(() => toInputValue(Date.now() - 30 * 60_000));
    const [to, setTo] = useState(() => toInputValue(Date.now()));

    const load = (nextPreset: Preset) => {
        const now = Date.now();
        const selected = PRESETS.find((candidate) => candidate.id === nextPreset);
        let range: TimeRange;
        if (nextPreset === "custom") {
            range = { fromMs: fromInputValue(from), toMs: fromInputValue(to) };
        } else {
            range = { fromMs: selected?.minutes ? now - selected.minutes * 60_000 : null, toMs: null };
            if (selected?.minutes) {
                setFrom(toInputValue(range.fromMs as number));
                setTo(toInputValue(now));
            }
        }
        setPreset(nextPreset);
        loadHistory(range);
    };

    useEffect(() => {
        load("30m");
        // Runs once when History is opened.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="shrink-0 border-b border-black80 bg-black100/80 px-3 py-2">
            <div className="flex flex-wrap items-center gap-1">
                {PRESETS.map((candidate) => (
                    <button key={candidate.id} type="button" onClick={() => load(candidate.id)} className={buttonClass(preset === candidate.id)}>
                        {candidate.label}
                    </button>
                ))}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-h5 text-black40">
                <label className="flex items-center gap-1">
                    From
                    <input
                        type="datetime-local"
                        value={from}
                        onChange={(event) => setFrom(event.target.value)}
                        className="rounded-r5 border border-black70 bg-black90 px-1 py-0.5 text-white"
                    />
                </label>
                <label className="flex items-center gap-1">
                    To
                    <input
                        type="datetime-local"
                        value={to}
                        onChange={(event) => setTo(event.target.value)}
                        className="rounded-r5 border border-black70 bg-black90 px-1 py-0.5 text-white"
                    />
                </label>
                <button type="button" onClick={() => load("custom")} className={buttonClass(preset === "custom")}>
                    Apply
                </button>
                <button type="button" onClick={() => load(preset)} className={buttonClass(false)}>
                    Refresh
                </button>
            </div>
            <p className="mt-1 text-[11px] text-black60">
                {history.loading
                    ? "Loading…"
                    : history.error
                      ? `Could not read stored logs: ${history.error}`
                      : `${history.entries.length} entries${history.truncated ? " (oldest omitted — narrow the range)" : ""}`}
            </p>
        </div>
    );
};

export default DevWebrtcHistoryBar;
